import React, { useCallback, useMemo } from "react"
import { useController, useFormContext } from "react-hook-form"
import { Table, Radio, Typography } from "@mimber/ui-web"
import type { ColumnsType } from "@mimber/ui-web"
import { Resource, Action } from "@mimber/schema"
import type {
  PermissionMatrixFieldProps,
  PermissionEntry,
} from "./PermissionMatrixField.types"
import {
  ASSIGNABLE_RESOURCES,
  RESOURCE_LABELS,
  STANDARD_ACTIONS,
  SEND_RESOURCES,
  type AssignableResource,
} from "./PermissionMatrixField.types"

const { Text } = Typography

const ACTION_LABELS: Record<string, string> = {
  READ: "Read",
  CREATE: "Create",
  UPDATE: "Update",
  DELETE: "Delete",
  MANAGE: "Manage",
  SEND: "Send",
}

interface ResourceRow {
  key: string
  resource: AssignableResource
  label: string
  supportsSend: boolean
}

const RESOURCE_ROWS: ResourceRow[] = ASSIGNABLE_RESOURCES.map((resource) => ({
  key: resource,
  resource,
  label: RESOURCE_LABELS[resource],
  supportsSend: SEND_RESOURCES.includes(resource),
}))

/**
 * PermissionMatrixField — A React Hook Form field that renders a matrix of
 * Resource rows × Action radio buttons using Ant Design Table.
 * One action per resource. "None" clears the resource entry.
 */
export const PermissionMatrixField: React.FunctionComponent<
  PermissionMatrixFieldProps
> = ({
  name,
  disabled = false,
  className,
  label = "Permissions",
  helpText,
}) => {
  const { control } = useFormContext()
  const {
    field: { value = [], onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: [],
  })

  /** Map of resource → selected action from current value */
  const selectedActions = useMemo(() => {
    const map: Record<string, string | null> = {}
    for (const perm of value as PermissionEntry[]) {
      map[perm.resource] = perm.action
    }
    return map
  }, [value])

  const handleChange = useCallback(
    (resource: AssignableResource, action: string | null) => {
      if (!onChange) return

      const filtered = (value as PermissionEntry[]).filter(
        (p) => p.resource !== resource
      )

      if (action) {
        filtered.push({
          resource: resource as Resource,
          action: action as Action,
        })
      }

      onChange(filtered)
    },
    [value, onChange]
  )

  const renderRadio = useCallback(
    (action: string, record: ResourceRow) => {
      const selected = selectedActions[record.resource] ?? null

      if (action === "SEND" && !record.supportsSend) {
        return (
          <Radio
            checked={false}
            disabled
            data-testid={`perm-${record.resource}-SEND`}
          />
        )
      }

      return (
        <Radio
          checked={selected === action}
          onChange={() => handleChange(record.resource, action)}
          disabled={disabled}
          data-testid={`perm-${record.resource}-${action}`}
        />
      )
    },
    [selectedActions, handleChange, disabled]
  )

  const columns: ColumnsType<ResourceRow> = useMemo(
    () => [
      {
        title: "Resource",
        dataIndex: "label",
        key: "resource",
        width: 180,
      },
      {
        title: <Text type="secondary">None</Text>,
        key: "NONE",
        align: "center" as const,
        width: 80,
        render: (_, record) => {
          const selected = selectedActions[record.resource] ?? null
          return (
            <Radio
              checked={selected === null}
              onChange={() => handleChange(record.resource, null)}
              disabled={disabled}
              data-testid={`perm-${record.resource}-NONE`}
            />
          )
        },
      },
      ...STANDARD_ACTIONS.map((action) => ({
        title: <Text type="secondary">{ACTION_LABELS[action]}</Text>,
        key: action,
        align: "center" as const,
        width: 80,
        render: (_: unknown, record: ResourceRow) =>
          renderRadio(action, record),
      })),
      {
        title: <Text type="secondary">Send</Text>,
        key: "SEND",
        align: "center" as const,
        width: 80,
        render: (_, record) => renderRadio("SEND", record),
      },
    ],
    [selectedActions, handleChange, disabled, renderRadio]
  )

  return (
    <div className={className}>
      {label && (
        <Text strong style={{ marginBottom: 8, display: "block" }}>
          {label}
        </Text>
      )}

      {helpText && (
        <Text type="secondary" style={{ marginBottom: 8, display: "block" }}>
          {helpText}
        </Text>
      )}

      <Table<ResourceRow>
        columns={columns}
        dataSource={RESOURCE_ROWS}
        pagination={false}
        size="small"
        bordered
      />

      {error && (
        <Text type="danger" style={{ marginTop: 4, display: "block" }}>
          {error.message}
        </Text>
      )}
    </div>
  )
}
